/**
 * PreferencesSection — Settings → Preferences
 *
 * Where JAI stops being one generic assistant and becomes the user's own
 * co-manager. Two kinds of setting live here:
 *
 *   Practical  — timezone, and which sport/league JAI opens on. Fantasy runs
 *                on deadlines, so "8pm" has to mean the user's 8pm.
 *
 *   Advisory   — risk appetite, how much working to show, and a free-text note
 *                about how they play. These are piped into the Claude system
 *                prompt (see backend claudeService.buildPreferenceBlock) and
 *                change the RECOMMENDATION, not just its wording: on a close
 *                call, "Bold" backs the higher ceiling and "Safe" backs the
 *                floor.
 *
 * They apply everywhere JAI gives an opinion — Ask JAI, Waiver Wire, the Trade
 * Analyser, the Draft Board — not just in chat.
 */

import { useEffect, useMemo, useState } from 'react'
import { accountApi, leaguesApi } from '../../services/api'
import { JAI_COLORS } from '../JaiBrand'
import { browserTimezone, formatClock } from '../../utils/datetime'
import { SelectRow, ChoiceCards, TextAreaField, SectionNote, Notice } from './SettingsFields'

/**
 * Timezones, straight from the browser's own database.
 *
 * Deliberately not a hand-maintained list: zones and their names change (a
 * country shifts its DST rules, a zone gets renamed), and a stale bundled list
 * would quietly start telling people the wrong deadline. Older browsers
 * without supportedValuesOf fall back to a short list covering where JAI's
 * users actually are.
 */
const TIMEZONES = (() => {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone')
    }
  } catch { /* fall through */ }
  return [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Perth',
    'Europe/London', 'UTC',
  ]
})()

/** "Wed 3:45 pm" in the given zone — so the choice is verifiable at a glance. */
const clockIn = (zone) => {
  try { return formatClock(new Date(), zone) } catch { return null }
}

const ADVICE_STYLES = [
  { value: 'bold',     label: 'Bold',     detail: 'Backs the higher ceiling on close calls. Upside over safety.' },
  { value: 'balanced', label: 'Balanced', detail: 'Weighs floor against ceiling and calls it straight.' },
  { value: 'safe',     label: 'Safe',     detail: 'Backs the steadier floor. Avoids boom-or-bust where it can.' },
]

const ADVICE_DETAILS = [
  { value: 'quick',    label: 'Quick call',      detail: 'Verdict and the few bullets that matter. The default.' },
  { value: 'detailed', label: 'Show the working', detail: 'More bullets, and the numbers behind the call spelled out.' },
]

export default function PreferencesSection({ details, onUpdated }) {
  const [leagues, setLeagues] = useState([])
  const [leaguesLoaded, setLeaguesLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    leaguesApi.getAll()
      .then(({ data }) => {
        if (cancelled) return
        setLeagues(Array.isArray(data) ? data : (data?.leagues || []))
      })
      .catch(() => { /* the dropdown just shows "No default" */ })
      .finally(() => { if (!cancelled) setLeaguesLoaded(true) })
    return () => { cancelled = true }
  }, [])

  const save = async (fields) => {
    const { data } = await accountApi.updatePreferences(fields)
    onUpdated(data.user)
  }

  const browserZone = useMemo(browserTimezone, [])
  const activeZone = details.timezone || browserZone
  const localTime = clockIn(activeZone)

  const timezoneOptions = useMemo(() => ([
    { value: '', label: browserZone ? `Use my device's timezone (${browserZone})` : 'Use my device\'s timezone' },
    ...TIMEZONES.map(z => ({ value: z, label: z.replace(/_/g, ' ') })),
  ]), [browserZone])

  const leagueOptions = useMemo(() => ([
    { value: '', label: 'No default — ask me each time' },
    ...leagues.map(l => ({
      value: String(l.id),
      label: `${l.name}${l.sport ? ` (${String(l.sport).toUpperCase()})` : ''}`,
    })),
  ]), [leagues])

  return (
    <div>
      {/* ── Practical ────────────────────────────────────────────────────── */}
      <SelectRow
        label="Timezone"
        value={details.timezone || ''}
        options={timezoneOptions}
        helper={localTime
          ? `It's ${localTime} for you right now. Every date and time in JAI is shown in this zone.`
          : 'Every date and time in JAI is shown in this zone.'}
        onSave={(v) => save({ timezone: v || null })}
      />

      <SelectRow
        label="Default sport"
        value={details.defaultSport || ''}
        options={[
          { value: '',    label: 'Let JAI work it out from my question' },
          { value: 'nfl', label: 'NFL' },
          { value: 'nba', label: 'NBA' },
        ]}
        helper="Which sport JAI assumes when you ask something without a league attached."
        onSave={(v) => save({ defaultSport: v || null })}
      />

      <SelectRow
        label="Default league"
        value={details.defaultLeagueId ? String(details.defaultLeagueId) : ''}
        options={leagueOptions}
        helper={leaguesLoaded && leagues.length === 0
          ? 'Connect a league and it\'ll show up here.'
          : 'The league JAI loads when you open the app. You can still switch at any time.'}
        onSave={(v) => save({ defaultLeagueId: v ? Number(v) : null })}
      />

      {/* ── How JAI advises you ──────────────────────────────────────────── */}
      <div style={{ marginTop: 26, paddingTop: 20, borderTop: `1px solid ${JAI_COLORS.hair}` }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: JAI_COLORS.navy }}>
          How JAI advises you
        </div>
        <SectionNote>
          These change the advice itself, not just the wording. They apply to
          every JAI recommendation — Ask JAI, Waiver Wire, the Trade Analyser
          and the Draft Room.
        </SectionNote>
      </div>

      <ChoiceCards
        label="Risk appetite"
        value={details.adviceStyle || 'balanced'}
        options={ADVICE_STYLES}
        onSave={(v) => save({ adviceStyle: v })}
        helper="When two options are genuinely close, this decides which way JAI leans."
      />

      <ChoiceCards
        label="How much detail"
        value={details.adviceDetail || 'quick'}
        options={ADVICE_DETAILS}
        onSave={(v) => save({ adviceDetail: v })}
      />

      <TextAreaField
        label="Anything JAI should know about how you play?"
        value={details.adviceNotes || ''}
        maxLength={500}
        placeholder="e.g. 12-team PPR dynasty, I'm rebuilding this year so I'd rather take on young talent than win now. I stream defences and never hold a kicker."
        helper="Written in your own words. JAI reads this on every question, so keep it to the things that actually change a decision."
        onSave={(v) => save({ adviceNotes: v })}
      />

      <Notice tone="neutral">
        None of this loosens JAI's rules about facts. Risk appetite changes
        which player JAI backs — it never changes whether JAI checks that the
        player is real, healthy and on the roster first.
      </Notice>

      {/* ── Emails ───────────────────────────────────────────────────────── */}
      <div style={{ marginTop: 26, paddingTop: 20, borderTop: `1px solid ${JAI_COLORS.hair}` }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: JAI_COLORS.navy }}>
          Emails
        </div>
      </div>

      <SelectRow
        label="Marketing emails"
        value={details.marketingEmails ? 'on' : 'off'}
        options={[
          { value: 'off', label: 'Off' },
          { value: 'on',  label: 'On — plans, new features and offers' },
        ]}
        helper="Optional. Account, security and billing emails are always sent, whatever you choose here."
        onSave={(v) => save({ marketingEmails: v === 'on' })}
      />

      <SelectRow
        label="Product updates"
        value={details.productUpdates ? 'on' : 'off'}
        options={[
          { value: 'on',  label: 'On — news about JAI itself' },
          { value: 'off', label: 'Off' },
        ]}
        helper="Occasional emails about JAI itself: new features, plan changes and founding pricing. Account, security and billing emails are always sent, whatever you choose here."
        onSave={(v) => save({ productUpdates: v === 'on' })}
      />
    </div>
  )
}
